import React, { useState } from 'react';
import { Block, Button, FormDatePicker, FormField, Modal } from 'components';
import { Field, Form, FormSpy } from 'react-final-form';
import { useStore } from 'react-redux';

import CouponImageCropper from 'components/Cropper/CouponImageCropper';
import LLMCouponsPreview from 'components/LLMCouponsPreview/LLMCouponsPreview';
import { COUPON_TECHNICAL_KEY } from 'containers/CouponBuilder/CouponBuilder';
import CouponCategoryField from 'containers/CouponCategoryField/CouponCategoryField';
import { COUPON_EXPIRY_TYPES, COUPON_TYPES } from 'coupon-common';
import { actions } from 'coupon-common/src/modules/couponBuilder';
import { actions as couponImageActions } from 'coupon-common/src/modules/couponImages';
import { dispatchAsAsync, required } from 'coupon-common/src/utils';


// ─── Image Picker Modal ───────────────────────────────────────────────────────
const ImagePickerModal = ({ isOpen, images, onSelect, onClose }) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen title="Choose a product image" onRequestClose={onClose}>
      <div style={{ maxHeight: 500, overflowY: 'auto' }}>
        {images.length === 0 ? (
          <p className="text-muted">No product images available.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, padding: 8 }}>
            {images.map((img, i) => (
              <div
                key={i}
                onClick={() => { onSelect(img.image_url); onClose(); }}
                style={{
                  cursor: 'pointer',
                  borderRadius: 6,
                  overflow: 'hidden',
                  border: '2px solid transparent',
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#0d6efd'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                title={img.title || ''}
              >
                <img
                  src={img.image_url}
                  alt={img.title || ''}
                  style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.parentElement.style.display = 'none'; }}
                />
              </div>
            ))}
          </div>
        )}
        <div className="text-end mt-2 pe-2 pb-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const AffiliateCouponsForm = ({
  coupon,
  index,
  affiliateBusinesses,
  selectedBusinessId,
  inputValue,
  selectedBusiness,
  dispatch,
  handleRemove,
  customCoupon = false,
  productImages = [],
  onImageChange = null,
  showDealFields = false,
  defaultCategory = null,
  editMode = false,
}) => {
  // console.log("cp", coupon)
  const store = useStore();
  const [createCouponLoading, setCreateCouponLoading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState(
    coupon?.image_url?.[0]?.image_url || null
  );
  // tracks an image the user explicitly uploaded/cropped —
  // always a data: URL, always takes priority over the form value
  const [replacedImageUrl, setReplacedImageUrl] = useState(null);

  const formatErrorMessage = (errorObj) => {
    if (!errorObj || typeof errorObj !== 'object') {
      return 'An unexpected error occurred.';
    }
    return Object.entries(errorObj)
      .flatMap(([field, errs]) =>
        Array.isArray(errs)
          ? errs.map((msg) => `${field}: ${msg}`)
          : `${field}: ${errs}`
      )
      .join('\n');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLocalImageUrl(ev.target.result);
      setReplacedImageUrl(ev.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCreateCoupon = async (values) => {
    if (!selectedBusinessId) return;

    if (!affiliateBusinesses.some(b => String(b.affiliate_code) === inputValue)) {
      alert('Add this business before trying to save the coupon.');
      return;
    }

    setCreateCouponLoading(true);

    try {
      // ── Step 1: Create the coupon first (no images yet) ──────────────────
      // We need the real coupon ID before we can create a CouponImage record.
      const payload = {
        ...values,
        business_id: selectedBusinessId,
        coupon_images: [],
        image_url: values.image_url,
      };

      const res = await dispatchAsAsync(
        actions.createCoupon(COUPON_TECHNICAL_KEY, payload),
        dispatch
      );

      if (!res?.success) {
        alert(formatErrorMessage(res?.error));
        setCreateCouponLoading(false);
        return;
      }
      addPublishedAffiliateCoupon(values.title || values.description);
      // ── Step 2: Retrieve the new coupon ID from the store ─────────────────
      // Mirrors the pattern used in CouponBuilderV1.handleFinalSubmit.
      const newCouponId = store.getState().couponBuilder?.[COUPON_TECHNICAL_KEY]?.data?.id;

      // ── Step 3: Upload the image against the real coupon ID ───────────────
      const imageUrl = replacedImageUrl || values.image_url?.[0]?.image_url || localImageUrl;

      if (newCouponId && imageUrl) {
        let imageData;

        if (imageUrl.startsWith('data:')) {
          // User uploaded or cropped — convert data URL to File
          const fetchRes = await fetch(imageUrl);
          const blob = await fetchRes.blob();
          imageData = new File([blob], `affiliate-${Date.now()}.jpg`, {
            type: blob.type || 'image/jpeg',
          });
        } else if (imageUrl.startsWith('http')) {
          // Remote URL (e.g. Rakuten CDN) — pass string directly.
          // api.uploadCouponImage handles strings via data.append('image', imageData)
          // and the backend's FlexibleImageField stores the URL as image.name.
          imageData = imageUrl;
        }

        if (imageData) {
          await dispatchAsAsync(
            couponImageActions.uploadCouponImage(1, newCouponId, 1, imageData),
            dispatch
          );
        }
      }

      alert('Coupon created!');
      handleRemove(values.localId);
    } catch (err) {
      alert(`Failed to create coupon: ${err.message}`);
    }

    setCreateCouponLoading(false);
  };



  const handleEditCoupon = async (values) => {
    setCreateCouponLoading(true);

    try {
      // Edit saga requires coupon builder to already have the original data of the coupon in the redux
      const fetchCoupon = await dispatchAsAsync(
        actions.getCoupon(values?.id),
        dispatch
      );

      if (!fetchCoupon?.success) {
        alert(formatErrorMessage(fetchCoupon?.error));
        return;
      }

      const payload = {
        ...values,
        image_url: values.image_url,
      };

      const res = await dispatchAsAsync(
        actions.editCoupon(values?.id, payload),
        dispatch
      );


      if (!res?.success) {
        alert(formatErrorMessage(res?.error));
        return;
      }
      addPublishedAffiliateCoupon(values.title || values.description);
      // Update image if needed
      const imageUrl =
        replacedImageUrl ||
        values.image_url?.[0]?.image_url ||
        localImageUrl;

      if (imageUrl) {
        let imageData;

        if (imageUrl.startsWith("data:")) {
          const fetchRes = await fetch(imageUrl);
          const blob = await fetchRes.blob();

          imageData = new File(
            [blob],
            `affiliate-${Date.now()}.jpg`,
            {
              type: blob.type || "image/jpeg",
            }
          );
        } else if (imageUrl.startsWith("http")) {
          imageData = imageUrl;
        }

        if (imageData) {
          await dispatchAsAsync(
            couponImageActions.uploadCouponImage(
              1,
              values.id,
              1,
              imageData
            ),
            dispatch
          );
        }
      }
      alert("Coupon updated!");
    } catch (err) {
      alert(`Failed to update coupon: ${err.message}`);
    }

    setCreateCouponLoading(false);
  };

  const handleCreateOrEditCoupons = (values) => {
    if (editMode) {
      handleEditCoupon(values)
    }
    else {
      handleCreateCoupon(values)
    }
  }

  const addPublishedAffiliateCoupon = (couponKey) => {
    // importing the key from AffiliateCouponsList was causing lint issues with import
    const PUBLISHED_AFFILIATE_STORAGE_KEY = 'publishedAffiliateCoupons'
    const publishedCoupons = new Set(
      JSON.parse(
        localStorage.getItem(PUBLISHED_AFFILIATE_STORAGE_KEY) || '[]'
      )
    );

    publishedCoupons.add(couponKey);

    localStorage.setItem(
      PUBLISHED_AFFILIATE_STORAGE_KEY,
      JSON.stringify([...publishedCoupons])
    );
  };

  const activeImageUrl = replacedImageUrl || coupon?.image_url?.[0]?.image_url || localImageUrl;

  //   console.log('[AffiliateCouponsForm] coupon:', JSON.stringify({
  //     id: coupon.id,
  //     expiry_type: coupon.expiry_type,
  //     published_at: coupon.published_at,
  //     expiration_date: coupon.expiration_date,
  //     expiration_quantity: coupon.expiration_quantity,
  // }));

  return (
    <Block>
      <span style={{ fontWeight: 'bold' }}>#{index + 1}</span>

      <Form
        initialValues={{ ...coupon, category: coupon?.category ?? defaultCategory }}
        onSubmit={handleCreateOrEditCoupons}
        render={({ handleSubmit, values }) => (
          <div className="mb-4">
            <div className="row">
              <div className="col-md-6" style={{ maxHeight: 400, overflowY: 'auto' }}>
                <form onSubmit={handleSubmit}>
                  <Field name="title" label="Title" component={FormField} validate={required} maxLength={36} />
                  <Field name="description" label="Description" component={FormField} validate={required} />
                  <Field name="price" label="Price" component={FormField} />

                  {/* {customCoupon && ( */}
                  <div>
                    <Field name="affiliate_link" label="Affiliate Link" component={FormField} validate={required} />

                    <div className='mb-2'>
                      <Field name="type" component="select" className="form-control">
                        <option value={COUPON_TYPES.PERCENTAGE_OFF}>Percentage Off</option>
                        <option value={COUPON_TYPES.AMOUNT_OFF}>Amount Off</option>
                      </Field>
                    </div>

                    {values.type === COUPON_TYPES.PERCENTAGE_OFF && (
                      <Field name="percentage_off" label="Percentage Off" component={FormField} validate={required} type="number" />
                    )}
                    {values.type === COUPON_TYPES.AMOUNT_OFF && (
                      <Field name="amount_off" label="Amount Off" component={FormField} validate={required} type="number" />
                    )}
                  </div>


                  {/* {!customCoupon && showDealFields && (
                    <div>
                      <div className='mb-2'>
                        <Field name="type" component="select" className="form-control">
                          <option value={COUPON_TYPES.PERCENTAGE_OFF}>Percentage Off</option>
                          <option value={COUPON_TYPES.AMOUNT_OFF}>Amount Off</option>
                        </Field>
                      </div>

                      {values.type === COUPON_TYPES.PERCENTAGE_OFF && (
                        <Field name="percentage_off" label="Percentage Off" component={FormField} type="number" />
                      )}
                      {values.type === COUPON_TYPES.AMOUNT_OFF && (
                        <Field name="amount_off" label="Amount Off" component={FormField} type="number" />
                      )}
                    </div>
                  )} */}

                  {coupon.expiry_type === COUPON_EXPIRY_TYPES.DATE ? (
                    <>
                      <Field name="published_at" label="Start Date" component={FormDatePicker} validate={required} />
                      <Field name="expiration_date" label="Expiration Date" component={FormDatePicker} validate={required} />
                    </>
                  ) : (
                    <Field name="expiration_quantity" label="Expiration Quantity" component={FormField} validate={required} type="number" />
                  )}

                  <CouponCategoryField />
                </form>
              </div>

              <div
                className="col-md-5"
                style={{ cursor: 'pointer' }}
                onClick={() => window.open(coupon?.link_url, '_blank')}
              >
                <LLMCouponsPreview
                  coupon={{
                    ...values,
                    image_url: activeImageUrl
                      ? [{ image_url: activeImageUrl, sequence: 1 }]
                      : values.image_url,
                  }}
                  business={selectedBusiness}
                />

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <label
                    style={{
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: '1px solid #6c757d',
                      background: '#fff',
                      fontSize: '14px',
                      color: '#6c757d',
                      display: 'inline-block',
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileSelect}
                    />
                    {activeImageUrl ? 'Replace image' : 'Upload image'}
                  </label>

                  {activeImageUrl && (
                    <button
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #dc3545',
                        background: '#fff',
                        fontSize: '14px',
                        color: '#dc3545',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCropperOpen(true);
                      }}
                    >
                      Edit image
                    </button>
                  )}

                  {productImages.length > 0 && onImageChange && (
                    <button
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #6c757d',
                        background: '#fff',
                        fontSize: '14px',
                        color: '#6c757d',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePickerOpen(true);
                      }}
                    >
                      Choose image
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Button outline onClick={() => handleRemove(coupon.localId)}>Remove</Button>
              <Button onClick={handleSubmit} fetching={createCouponLoading}>Save</Button>
            </div>

            <FormSpy subscription={{ submitFailed: true, hasValidationErrors: true }}>
              {({ submitFailed, hasValidationErrors }) =>
                submitFailed && hasValidationErrors ? (
                  <label style={{ marginLeft: 12, color: '#de3e4e' }}>
                    Some required fields are missing or invalid.
                  </label>
                ) : null
              }
            </FormSpy>
          </div>
        )}
      />

      {cropperOpen && (
        <Modal isOpen title="Edit coupon image" onRequestClose={() => setCropperOpen(false)}>
          <CouponImageCropper
            image={activeImageUrl}
            onSave={({ url }) => {
              setLocalImageUrl(url);
              setReplacedImageUrl(url);
              if (coupon.image_url?.[0]) {
                coupon.image_url[0].image_url = url;
              }
              setCropperOpen(false);
            }}
            onClose={() => setCropperOpen(false)}
          />
        </Modal>
      )}

      <ImagePickerModal
        isOpen={imagePickerOpen}
        images={productImages}
        onSelect={(url) => {
          if (onImageChange) onImageChange(coupon.localId, url);
        }}
        onClose={() => setImagePickerOpen(false)}
      />
    </Block>
  );
};

export default AffiliateCouponsForm;